import React from 'react';
import styled, { keyframes } from 'styled-components';
import { MediaQuery, Palette } from 'core/config';
import { getContactUsUrl } from 'core/utilities';
import { showCaseImages, emrShowcaseImages, estateShowcaseImages, fixmeShowcaseImages } from '../data/showCaseImages.json';
import IMAGES from 'assets/images';

const AUDIT_TITLE =
    'Your idea isn\'t the problem—\nthe foundation is.';
const AUDIT_A_TITLE =
    'It worked at launch—\nbut it won\'t scale like this.';
const EMR_TITLE =
    'Your systems don\'t work together.\nYour clinic pays for it.';
const AUDIT_SUBTITLE =
    'We bring the funding strategy, security rigor, and senior technical leadership required to scale.';
const EMR_SUBTITLE =
    'We use AI to connect your EMR, scheduling, and clinic tools—so workflows run smoothly and patients aren\'t left waiting.';
const ESTATE_TITLE =
    'AI is studying you—\nmore than ever before.';
const ESTATE_SUBTITLE =
    'We clean your digital footprint and secure your life.';
const ESTATE_1_TITLE =
    'AI analyzes your digital life\nat a depth never seen before.';
const ESTATE_1_SUBTITLE =
    'We reduce your exposure and deliver complete security for you and your family.';
const EXPOSURE_REPORT_TITLE =
    'AI is predicting your behavior—based on everything you do.';
const EXPOSURE_REPORT_SUBTITLE =
    'Your digital identity is built from your activity, your data, and your exposure online. We show you what\'s visible—and remove what shouldn\'t be.';
const FIXME_TITLE =
    'Your codebase is broken.\nWe\'ll fix it.';
const FIXME_SUBTITLE =
    'We diagnose and resolve the architectural debt, performance bottlenecks, and security gaps that are slowing your team down.';
const THANK_YOU_TITLE = 'Your meeting is booked!';
const THANK_YOU_SUBTITLE =
    'Thank you for booking with us. We\'re excited to potentially build with you.';

interface HeroProps {
    navigateToSection?: (index: number) => void;
    /** Landing variants — Trusted By label, Calendly CTA */
    variant?: 'default' | 'thank-you' | 'audit' | 'audit-a' | 'emr' | 'estate' | 'estate-1' | 'exposure-report' | 'fixme';
    titleOverride?: string;
    subtitleOverride?: React.ReactNode;
}

export const Hero: React.FunctionComponent<HeroProps> = ({
    navigateToSection = () => null,
    variant = 'default',
    titleOverride,
    subtitleOverride,
}) => 
{
    const isThankYou = variant === 'thank-you';
    const isAudit = variant !== 'default' && !isThankYou;

    const titleText = titleOverride
        ?? (variant === 'thank-you'
            ? THANK_YOU_TITLE
            : variant === 'audit-a'
                ? AUDIT_A_TITLE
                : variant === 'emr'
                    ? EMR_TITLE
                    : variant === 'estate'
                        ? ESTATE_TITLE
                        : variant === 'exposure-report'
                            ? EXPOSURE_REPORT_TITLE
                            : variant === 'estate-1'
                                ? ESTATE_1_TITLE
                                : variant === 'fixme'
                                    ? FIXME_TITLE
                                    : variant === 'audit'
                                        ? AUDIT_TITLE
                                        : 'Your AI Product and Software Partner.');

    const subtitleText = subtitleOverride
        ?? (variant === 'thank-you'
            ? THANK_YOU_SUBTITLE
            : variant === 'emr'
                ? EMR_SUBTITLE
                : variant === 'estate'
                    ? ESTATE_SUBTITLE
                    : variant === 'exposure-report'
                        ? EXPOSURE_REPORT_SUBTITLE
                        : variant === 'estate-1'
                            ? ESTATE_1_SUBTITLE
                            : variant === 'fixme'
                                ? FIXME_SUBTITLE
                                : isAudit
                                    ? AUDIT_SUBTITLE
                                    : 'Designing and developing digital products and experiences for innovative companies, globally.');

    const auditCtaHref =
        variant === 'exposure-report'
            ? '/exposure-assessment'
            : getContactUsUrl({
                intent:
                    variant === 'emr'
                        ? 'emr'
                        : variant === 'estate'
                            ? 'estate'
                            : variant === 'estate-1'
                                ? 'estate-1'
                                : variant === 'fixme'
                                    ? 'fixme'
                                    : 'audit',
            });

    return (
        <Container>
            <InnerContainer className="fade-in">
                <Content>
                    <TextContent>
                        <Title>{titleText}</Title>
                        <Subtitle>
                            {subtitleText}
                        </Subtitle>
                    </TextContent>

                    {isAudit ? <TrustedByLabel>Trusted By:</TrustedByLabel> : null}

                    <ShowcaseContent $audit={isAudit}>
                        {(variant === 'emr' ? emrShowcaseImages
                            : variant === 'estate' || variant === 'estate-1' || variant === 'exposure-report' ? estateShowcaseImages
                                : variant === 'fixme' ? fixmeShowcaseImages
                                    : showCaseImages
                        ).map((item, index) => (
                            <ShowcaseImage
                                key={item.id}
                                src={IMAGES[item.imageKey]}
                                alt={`Showcase image ${index + 1}`}
                                $emr={variant === 'emr' || variant === 'estate' || variant === 'estate-1' || variant === 'exposure-report' || variant === 'fixme'}
                            />
                        ))}
                    </ShowcaseContent>

                    {isAudit ? (
                        <CtaLink href={auditCtaHref}>
                            {variant === 'estate'
                                ? 'Start My Confidential Audit'
                                : variant === 'estate-1'
                                    ? 'See What AI Knows About Me'
                                    : variant === 'exposure-report'
                                        ? 'Get My Exposure Report'
                                        : variant === 'emr' || variant === 'fixme'
                                            ? 'Book your Consultation Now'
                                            : 'Book your Free Audit'}
                        </CtaLink>
                    ) : isThankYou ? null : (
                        <Button onClick={() => navigateToSection(1)}>See Our Work</Button>
                    )}
                </Content>
            </InnerContainer>
        </Container>
    );
};

const fadeIn = keyframes`
    0% { opacity: 0 } 
    100% { opacity: 1 } 
`;

const Container = styled.div`
  z-index: 5000;
  display: flex;
  margin: 0 16px 0 16px;

  /* background-color: white; */
  overflow: hidden;

  .fade-in {
    animation: ${fadeIn} 0.5s ease-in;
  }

  @media ${MediaQuery.tablet} {
    min-width: 100vw;
    min-height: 90vh;
  }
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: center;

  width: 100%;
  height: 100%;

  .fade-in-sphere {
    transition-duration: 0.7s;
    opacity: 1;
  }
  .fade-out-sphere {
    opacity: 0;
  }

  @media ${MediaQuery.tablet} {
    flex-direction: row;
    padding: 3rem 5rem;
  }
`;

const Content = styled.div`
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
`;

const TextContent = styled.div`
  padding-top: 0;
  @media ${MediaQuery.tablet} {
    max-width: 45vh;
    padding-top: 0;
    margin-left: 5%;
  }

  @media ${MediaQuery.laptopM} {
    max-width: 70vh;
  }
`;

const Title = styled.h1`
  width: 100%;

  color: ${Palette.BLACK};

  font-size: calc(1.325em + 4vw);
  line-height: 1.15em;
  white-space: pre-line;

  @media ${MediaQuery.tablet} {
    width: 40vw;
    font-size: 2.75em;
  }
  @media ${MediaQuery.laptopS} {
    font-size: 3.375em;
  }
  @media ${MediaQuery.desktopS} {
    width: 100%;
    font-size: 4em;
  }
`;

const Subtitle = styled.h4`
  width: 100%;

  color: ${Palette.BLACK};
  font-family: "Poppins-Light";

  font-size: 16px;
  line-height: 180%;
  white-space: pre-line;
  padding-top: 24px;

  @media ${MediaQuery.tablet} {
    width: 40vw;
    font-size: 18px;
  }
  @media ${MediaQuery.laptopS} {
    width: 40vw;
    font-size: 20px;
    padding-top: 14px;
  }
  @media ${MediaQuery.desktopS} {
    width: 40vw;
    font-size: 22px;
    padding-top: 14px;
  }
`;

const TrustedByLabel = styled.p`
  width: 100%;
  margin: 0;
  margin-top: 40px;
  padding-top: 0;
  font-family: 'Poppins-Medium', sans-serif;
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${Palette.GREY};

  @media ${MediaQuery.tablet} {
    margin-left: 5%;
    margin-top: 48px;
  }

  @media ${MediaQuery.laptopS} {
    margin-top: 56px;
  }
`;

const ShowcaseContent = styled.div<{ $audit?: boolean }>`
  max-width: 90vw;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  gap: 48px;
  padding-top: ${({ $audit }) => ($audit ? 28 : 48)}px;
  padding-bottom: 48px;

  &::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbars for Firefox */
  scrollbar-width: none;

  @media ${MediaQuery.tablet} {
    margin-left: 5%;
    flex-wrap: nowrap;
    padding-top: ${({ $audit }) => ($audit ? 24 : 48)}px;
  }
`;

const ShowcaseImage = styled.img<{ $emr?: boolean }>`
  width: ${({ $emr }) => ($emr ? '120px' : '90px')};
  height: ${({ $emr }) => ($emr ? '32px' : '24px')};
  object-fit: contain;
`;

const Button = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
    -webkit-tap-highlight-color: transparent;

  width: 213px;

  border: 1px solid ${Palette.BLACK};
  background-color: ${Palette.BLACK};
  color: ${Palette.WHITE};

  font-family: "Poppins-Light";
  font-size: 12px;

  cursor: pointer;
  transition: opacity 1s ease-in-out;
  border-radius: 50px;
  padding: 14px 48px;

  :hover {
    background-color: ${Palette.WHITE};
    color: ${Palette.BLACK};
  }

  @media ${MediaQuery.tablet} {
    font-family: "Poppins-Regular";
    font-size: 17px;
    margin-left: 5%;
  }
`;

const CtaLink = styled.a`
  position: relative;
  z-index: 10;
  display: flex;
  justify-content: center;
  align-items: center;
  -webkit-tap-highlight-color: transparent;
  align-self: flex-start;
  width: auto;
  min-width: 213px;
  max-width: 100%;
  text-align: center;
  text-decoration: none;

  border: 1px solid ${Palette.BLACK};
  background-color: ${Palette.BLACK};
  color: ${Palette.WHITE};

  font-family: "Poppins-Light";
  font-size: 12px;

  cursor: pointer;
  transition: opacity 1s ease-in-out, background-color 0.2s ease, color 0.2s ease;
  border-radius: 50px;
  padding: 14px 28px;

  :hover {
    background-color: ${Palette.WHITE};
    color: ${Palette.BLACK};
  }

  @media ${MediaQuery.tablet} {
    font-family: "Poppins-Regular";
    font-size: 17px;
    margin-left: 5%;
  }
`;
